/**
 * <LiveCostMeter /> — terminal-style streaming console of the most recent
 * proxied LLM requests. Polls `/api/llm-proxy/requests?limit=10` and
 * `/api/llm-proxy/stats` and animates new rows in like `tail -f` of your
 * AI bill.
 *
 * History
 *   v34.4 (Mar 2026) — original drop, inspired by the FinOps gateway
 *                      tutorial `print(f"💰 FINOPS LOG: {entry}")` line.
 *   v34.7 (May 2026) — P1 POLISH (this revision):
 *     • CRITICAL BUG FIX: API returns `{count, items}` but the previous
 *       version was iterating `r.data` directly, so no rows EVER rendered
 *       and the meter was permanently stuck on "Waiting...".
 *     • Header now shows running totals (cost · tokens · req count · p95)
 *       that animate as new rows stream in. Pulled from /llm-proxy/stats.
 *     • Per-row enhancements:
 *         · Provider icon dot (OpenAI green, Anthropic orange, Google blue,
 *           Meta navy, others slate) so eyes can scan by vendor at a glance.
 *         · Sparkline cost bar on the right that scales to the most
 *           expensive request in the window (visual "this request hurt").
 *         · Error / cache badges when the request errored / hit the cache.
 *     • Adaptive polling: 2.5s when new rows streaming in, 8s when idle,
 *       paused entirely when the browser tab is hidden — saves API calls.
 *     • Empty state now shows a copy-pastable curl that hits the user's
 *       own proxy + a quick-link to the snippet section below.
 *     • Schema fix: uses `tenant_label` (the real field) instead of the
 *       phantom `tenant_id` / `x_cartie_tenant` that never existed.
 *
 * Lives at the top of /admin/llm-proxy so visitors immediately see the
 * "every request is tracked" promise as it happens.
 */
import React, { useEffect, useRef, useState, useCallback } from 'react';
import axios from 'axios';
import { Activity, Copy, Check, AlertCircle, Zap } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

const POLL_FAST_MS = 2500;   // when traffic is fresh
const POLL_SLOW_MS = 8000;   // when nothing's changed
const STATS_POLL_MS = 6000;  // running totals refresh

// ───────────────────────────────────────────────────────────────────
// Formatters
// ───────────────────────────────────────────────────────────────────
const fmtCost = (n) => {
  if (n == null || isNaN(n)) return '$0.0000';
  if (n >= 1)    return `$${n.toFixed(2)}`;
  if (n >= 0.01) return `$${n.toFixed(4)}`;
  return `$${n.toFixed(6)}`;
};

const fmtCostTotal = (n) => {
  if (n == null || isNaN(n)) return '$0.00';
  if (n >= 1000) return `$${(n / 1000).toFixed(2)}K`;
  if (n >= 1)    return `$${n.toFixed(2)}`;
  return `$${n.toFixed(4)}`;
};

const fmtCount = (n) => {
  if (n == null) return '0';
  if (n >= 1000) return `${(n / 1000).toFixed(1)}k`;
  return String(n);
};

const fmtTime = (iso) => {
  try {
    const d = new Date(iso);
    return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false });
  } catch { return '--:--:--'; }
};

// Map provider/model → a stable accent dot color for at-a-glance vendor scan.
const providerDot = (provider, model = '') => {
  const p = (provider || '').toLowerCase();
  const m = (model || '').toLowerCase();
  if (p === 'openai'    || m.startsWith('gpt-') || m.startsWith('o1')   || m.startsWith('o3')) return 'bg-emerald-400';
  if (p === 'anthropic' || m.startsWith('claude'))                                              return 'bg-amber-400';
  if (p === 'google'    || m.startsWith('gemini'))                                              return 'bg-sky-400';
  if (p === 'meta'      || m.startsWith('llama'))                                               return 'bg-indigo-400';
  if (p === 'mistral'   || m.startsWith('mistral'))                                             return 'bg-rose-400';
  return 'bg-slate-500';
};

const providerLabel = (provider, model = '') => {
  // Trim noisy prefixes (`anthropic/claude-3-5`, `openai/gpt-4`) for the row.
  return (model || 'unknown').replace(/^(openai|anthropic|google|meta|mistral)\//, '').slice(0, 18);
};

// ───────────────────────────────────────────────────────────────────
// Component
// ───────────────────────────────────────────────────────────────────

// Latency tier color — fast (green) → slow (rose). Visual scan, no math.
const latencyTone = (ms) => {
  const n = Number(ms) || 0;
  if (n < 200)  return 'text-emerald-300';
  if (n < 800)  return 'text-amber-300';
  if (n < 2000) return 'text-orange-400';
  return 'text-rose-400 font-bold';
};

// Stable hash so the same tenant always gets the same accent across renders.
const TENANT_ACCENTS = ['text-emerald-300', 'text-amber-200', 'text-sky-300', 'text-violet-300', 'text-pink-300', 'text-teal-300', 'text-rose-300', 'text-indigo-300'];
const tenantTone = (label) => {
  if (!label || label === 'default') return 'text-slate-400';
  let h = 0;
  for (let i = 0; i < label.length; i++) h = ((h << 5) - h + label.charCodeAt(i)) | 0;
  return TENANT_ACCENTS[Math.abs(h) % TENANT_ACCENTS.length];
};

const LiveCostMeter = () => {
  const [rows, setRows] = useState([]);
  const [stats, setStats] = useState(null);
  const [running, setRunning] = useState(true);
  const [errorMsg, setErrorMsg] = useState(null);
  const [pollMs, setPollMs] = useState(POLL_FAST_MS);
  const [copied, setCopied] = useState(false);
  const seenIds = useRef(new Set());

  // ────────────────────────────────────────────
  // Fetch loop — adaptive polling cadence.
  // ────────────────────────────────────────────
  const pullRequests = useCallback(async () => {
    if (typeof document !== 'undefined' && document.hidden) return;
    try {
      const r = await axios.get(`${API}/llm-proxy/requests?limit=10`, auth());
      // BUG FIX (v34.7): backend returns `{count, items}` not a bare array.
      const payload = r.data || {};
      const items = Array.isArray(payload) ? payload : (payload.items || []);
      const fresh = items.filter(x => !seenIds.current.has(x.req_id || x.id));
      fresh.forEach(x => seenIds.current.add(x.req_id || x.id));
      if (fresh.length) {
        setRows(prev => [...fresh, ...prev].slice(0, 10));
        setPollMs(POLL_FAST_MS);
      } else {
        // No new rows — slow down to save bandwidth (but keep showing existing).
        setRows(prev => (prev.length === 0 && items.length ? items.slice(0, 10) : prev));
        setPollMs((cur) => Math.min(cur + 1500, POLL_SLOW_MS));
      }
      setErrorMsg(null);
    } catch (e) {
      // 401 = not authed (read-only public view), don't shout
      const status = e?.response?.status;
      if (status && status !== 401 && status !== 403) {
        setErrorMsg(`proxy unreachable (${status})`);
      }
    }
  }, []);

  const pullStats = useCallback(async () => {
    if (typeof document !== 'undefined' && document.hidden) return;
    try {
      const r = await axios.get(`${API}/llm-proxy/stats`, auth());
      setStats(r.data || null);
    } catch { /* silent */ }
  }, []);

  useEffect(() => {
    let reqTimer, statsTimer, cancelled = false;
    const reqLoop = () => {
      if (cancelled || !running) return;
      pullRequests().finally(() => {
        reqTimer = setTimeout(reqLoop, pollMs);
      });
    };
    const statsLoop = () => {
      if (cancelled || !running) return;
      pullStats().finally(() => {
        statsTimer = setTimeout(statsLoop, STATS_POLL_MS);
      });
    };
    reqLoop();
    statsLoop();

    // Resume when the tab becomes visible again
    const onVisibility = () => { if (!document.hidden && running) { pullRequests(); pullStats(); } };
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      cancelled = true;
      clearTimeout(reqTimer);
      clearTimeout(statsTimer);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [running, pollMs, pullRequests, pullStats]);

  // ────────────────────────────────────────────
  // Sparkline reference for visual cost scaling
  // ────────────────────────────────────────────
  const maxCost = Math.max(0.0001, ...rows.map(r => Number(r.cost_usd) || 0));

  // ────────────────────────────────────────────
  // Copy snippet for empty state
  // ────────────────────────────────────────────
  const snippet = `curl ${window.location.origin || 'https://cartieai.com'}/api/llm-proxy/v1/chat/completions \\
  -H "X-Cartie-Key: <YOUR_PROXY_KEY>" \\
  -H "Content-Type: application/json" \\
  -d '{"model":"gpt-4o-mini","messages":[{"role":"user","content":"hi"}]}'`;

  const onCopy = () => {
    navigator.clipboard.writeText(snippet);
    setCopied(true);
    setTimeout(() => setCopied(false), 1800);
  };

  return (
    <div className="rounded-xl border border-emerald-400/50 bg-slate-950 overflow-hidden shadow-xl shadow-emerald-500/10 font-mono" data-testid="live-cost-meter">
      {/* Header bar */}
      <div className="flex items-center justify-between gap-3 px-4 py-2 border-b border-emerald-500/30 bg-slate-900 bg-gradient-to-r from-slate-900 via-emerald-950/40 to-slate-900 flex-wrap">
        <div className="flex items-center gap-2.5 min-w-0">
          <span className="w-2.5 h-2.5 rounded-full bg-rose-500 shrink-0" />
          <span className="w-2.5 h-2.5 rounded-full bg-amber-400 shrink-0" />
          <span className="w-2.5 h-2.5 rounded-full bg-emerald-400 shrink-0" />
          <span className="ml-2 text-[10px] uppercase tracking-widest text-emerald-300/70 truncate">cartie@finops:~ $ tail -f <span className="text-emerald-200">/llm-proxy/live</span></span>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <span className="relative flex h-2 w-2">
            <span className={`absolute inline-flex h-full w-full rounded-full ${running ? 'animate-ping bg-emerald-400 opacity-75' : 'bg-slate-600'}`} />
            <span className={`relative inline-flex rounded-full h-2 w-2 ${running ? 'bg-emerald-400' : 'bg-slate-600'}`} />
          </span>
          <button
            type="button"
            onClick={() => setRunning(r => !r)}
            className={`text-[10px] uppercase tracking-widest transition-colors ${running ? 'text-emerald-300 hover:text-emerald-200' : 'text-amber-300 hover:text-amber-200'}`}
            data-testid="live-meter-toggle"
          >
            {running ? 'LIVE' : 'PAUSED'}
          </button>
        </div>
      </div>

      {/* ─────────────────────────────────────────────────────────────
          Running totals strip — pulled from /llm-proxy/stats every 6s.
          Animates as rows stream in. Tabular nums so the digits don't
          jitter horizontally during count-up.
          ───────────────────────────────────────────────────────────── */}
      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-px bg-emerald-500/20 border-b border-emerald-500/30" data-testid="live-meter-totals">
          {[
            { label: 'total cost',  value: fmtCostTotal(stats.total_cost_usd),   accent: 'text-emerald-300', bg: 'bg-slate-950 bg-gradient-to-br from-emerald-950/80 to-slate-950' },
            { label: 'tokens',      value: fmtCount(stats.total_tokens),         accent: 'text-amber-300',   bg: 'bg-slate-950 bg-gradient-to-br from-amber-950/60 to-slate-950' },
            { label: 'requests',    value: fmtCount(stats.request_count),        accent: 'text-sky-300',     bg: 'bg-slate-950 bg-gradient-to-br from-sky-950/60 to-slate-950' },
            { label: 'p95 latency', value: `${stats.p95_latency_ms || 0}ms`,     accent: 'text-rose-300',    bg: 'bg-slate-950 bg-gradient-to-br from-rose-950/60 to-slate-950' },
          ].map((t) => (
            <div key={t.label} className={`${t.bg} px-3 py-2.5`}>
              <div className="text-[9px] uppercase tracking-[0.18em] text-slate-400">{t.label}</div>
              <div className={`text-base font-bold tabular-nums tracking-tight ${t.accent}`}>{t.value}</div>
            </div>
          ))}
        </div>
      )}

      {/* Optional error strip */}
      {errorMsg && (
        <div className="px-4 py-2 bg-rose-500/10 border-b border-rose-500/30 flex items-center gap-2 text-[11px] text-rose-300" data-testid="live-meter-error">
          <AlertCircle className="w-3.5 h-3.5" />
          <span>{errorMsg}</span>
        </div>
      )}

      {/* Stream body */}
      <div className="px-4 py-3 max-h-[280px] overflow-auto" data-testid="live-meter-stream">
        {rows.length === 0 ? (
          // ─────────────────────────────────────────────────────────
          // Empty state — actionable, not just "waiting...".
          // Shows a copy-pastable curl so the dev can trigger their
          // first request right from this surface.
          // ─────────────────────────────────────────────────────────
          <div className="py-6">
            <div className="flex items-center gap-2 mb-3">
              <Activity className="w-4 h-4 text-emerald-400" />
              <p className="text-[11px] text-slate-400">Waiting for your first proxied request — drop this curl in your terminal:</p>
            </div>
            <div className="relative bg-slate-900 border border-slate-800 rounded-md p-3 text-[11px] leading-relaxed text-slate-300 whitespace-pre-wrap break-all" data-testid="live-meter-empty-snippet">
              {snippet}
              <button
                type="button"
                onClick={onCopy}
                className="absolute top-2 right-2 inline-flex items-center gap-1 px-2 py-1 rounded bg-slate-800 hover:bg-slate-700 text-emerald-300 text-[10px] uppercase tracking-widest transition-colors"
                data-testid="live-meter-copy-snippet"
              >
                {copied ? (<><Check className="w-3 h-3" /> copied</>) : (<><Copy className="w-3 h-3" /> copy</>)}
              </button>
            </div>
            <p className="text-[10px] text-slate-600 mt-2">Tip — replace <code className="text-emerald-300">&lt;YOUR_PROXY_KEY&gt;</code> with a key from the panel below. The row will stream in here within ~2 seconds.</p>
          </div>
        ) : (
          <ul className="space-y-1.5 text-[12px] leading-relaxed">
            {rows.map((r, idx) => {
              const tenant = r.tenant_label || 'default';
              const status = r.status_code || 200;
              const ok = status < 400;
              const errored = !!r.error || !ok;
              const cost = Number(r.cost_usd) || 0;
              const barPct = Math.max(2, Math.round((cost / maxCost) * 100));
              return (
                <li
                  key={r.req_id || r.id || `${r.created_at}-${idx}`}
                  className="grid grid-cols-12 gap-2 items-center border-b border-emerald-500/10 pb-1.5 last:border-0 animate-fade-in-up hover:bg-emerald-500/[0.04] transition-colors px-1 -mx-1 rounded"
                  data-testid={`live-meter-row-${idx}`}
                >
                  {/* time */}
                  <span className="col-span-2 sm:col-span-1 text-cyan-300/80 tabular-nums">{fmtTime(r.created_at)}</span>

                  {/* provider dot + model */}
                  <span className="col-span-3 sm:col-span-3 flex items-center gap-1.5 min-w-0">
                    <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${providerDot(r.provider, r.model)}`} />
                    <span className="text-slate-100 truncate font-semibold" title={r.model}>{providerLabel(r.provider, r.model)}</span>
                  </span>

                  {/* tenant — colored by stable hash so eyes can scan */}
                  <span className={`col-span-2 sm:col-span-2 truncate ${tenantTone(tenant)}`} title={tenant}>{tenant}</span>

                  {/* tokens — in→out, color split for visual interest */}
                  <span className="col-span-2 tabular-nums">
                    <span className="text-sky-300">{r.prompt_tokens || 0}</span>
                    <span className="text-slate-500">→</span>
                    <span className="text-amber-300">{r.completion_tokens || 0}</span>
                  </span>

                  {/* cost + sparkline scaled bar */}
                  <span className="col-span-2 sm:col-span-2 relative">
                    <span className="absolute inset-y-0 left-0 bg-gradient-to-r from-emerald-500/30 to-emerald-400/10 rounded-sm" style={{ width: `${barPct}%` }} />
                    <span className="relative pl-1 pr-1 text-emerald-200 font-bold tabular-nums">{fmtCost(cost)}</span>
                  </span>

                  {/* latency + status badge */}
                  <span className="col-span-1 sm:col-span-2 flex items-center justify-end gap-1.5">
                    {errored && (
                      <span className="text-[9px] uppercase tracking-widest bg-rose-500/25 text-rose-200 border border-rose-500/40 px-1 py-0.5 rounded-sm" title={r.error || `HTTP ${status}`}>
                        err
                      </span>
                    )}
                    {r.cache_hit && (
                      <span className="text-[9px] uppercase tracking-widest bg-emerald-500/25 text-emerald-200 border border-emerald-500/40 px-1 py-0.5 rounded-sm inline-flex items-center gap-0.5">
                        <Zap className="w-2.5 h-2.5" /> cache
                      </span>
                    )}
                    <span className={`text-right tabular-nums text-[11px] ${ok ? latencyTone(r.latency_ms) : 'text-rose-400 font-bold'}`}>{r.latency_ms || 0}ms</span>
                  </span>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {/* Footer */}
      <div className="px-4 py-2 border-t border-emerald-500/30 bg-slate-900 bg-gradient-to-r from-slate-900 via-emerald-950/40 to-slate-900 flex items-center justify-between flex-wrap gap-2">
        <p className="text-[10px] text-emerald-300/70 font-mono">
          <span className="text-emerald-200 font-bold">{rows.length}/10</span> rows · refresh <span className="text-amber-300">{Math.round(pollMs / 1000)}s</span> · stream is live, not sampled
        </p>
        <p className="text-[10px] text-emerald-300 font-mono">
          like <code className="text-amber-300">tail -f</code> for your AI bill
        </p>
      </div>
    </div>
  );
};

export default LiveCostMeter;
